import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

interface EsQueryDialogProps {
  open: boolean;
  esQuery: Record<string, unknown> | null;
  onClose: () => void;
  onSubmit: (editedQuery: Record<string, unknown>, size: number) => void;
}

export default function EsQueryDialog({
  open,
  esQuery,
  onClose,
  onSubmit,
}: EsQueryDialogProps) {
  const [text, setText] = useState(() =>
    esQuery ? JSON.stringify(esQuery, null, 2) : "",
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [size, setSize] = useState(20);

  useEffect(() => {
    if (open) {
      setText(esQuery ? JSON.stringify(esQuery, null, 2) : "");
      setParseError(null);
    }
  }, [open, esQuery]);

  const handleSubmit = () => {
    try {
      const parsed = JSON.parse(text);
      setParseError(null);
      onSubmit(parsed, size);
    } catch (e: unknown) {
      setParseError(e instanceof Error ? e.message : "Invalid JSON");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit &amp; Resubmit ES Query</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Edit the Elasticsearch query below and resubmit. The query is sent
          directly to ES — changes here bypass the normal search parameter
          pipeline.
        </Typography>
        <TextField
          multiline
          fullWidth
          minRows={18}
          maxRows={30}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setParseError(null);
          }}
          error={!!parseError}
          helperText={parseError}
          InputProps={{
            sx: {
              fontFamily: "monospace",
              fontSize: 12,
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <TextField
          label="Size"
          type="number"
          size="small"
          value={size}
          onChange={(e) => setSize(Math.max(1, Number(e.target.value)))}
          sx={{ width: 80 }}
          inputProps={{ min: 1 }}
        />
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Resubmit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
